import React, { useMemo } from 'react';
import { Note } from '@/store/notesStore';
import { extractNoteExcerpt } from '@/utils/noteDisplayText';
import { highlightText } from '@/utils/searchHighlight';

// ─── per-content-type excerpt components ────────────────────────────────────

/** MDX: extract via renderMarkdownSafe → DOM textContent (handles :secret[...]) */
const MDXExcerpt: React.FC<{ note: Note; searchQuery?: string }> = ({ note, searchQuery }) => {
  const text = useMemo(() => extractNoteExcerpt(note), [note]);
  return (
    <span className="italic text-gray-400 dark:text-gray-500 truncate">
      {text ? highlightText(text, searchQuery) : <span>No content</span>}
    </span>
  );
};

/** TXT: plain text slice */
const TxtExcerpt: React.FC<{ note: Note; searchQuery?: string }> = ({ note, searchQuery }) => {
  const text = useMemo(() => extractNoteExcerpt(note), [note]);
  return (
    <span className="italic text-gray-400 dark:text-gray-500 truncate">
      {text ? highlightText(text, searchQuery) : <span>No content</span>}
    </span>
  );
};

/** Encrypted: show placeholder */
const EncryptedExcerpt: React.FC = () => (
  <span className="italic text-gray-400 dark:text-gray-500 select-none">
    [Encrypted]
  </span>
);

// ─── main component ──────────────────────────────────────────────────────────

interface NoteListTitleProps {
  note: Note;
  searchQuery?: string;
}

/**
 * Renders the best available label for a note row in list view.
 *
 * - If the note has a title: display it in normal weight.
 * - Otherwise: dispatch to a per-content-type excerpt component that produces
 *   a plain-text preview (mirrors the PreviewNoteContent pattern).
 */
const NoteListTitle: React.FC<NoteListTitleProps> = ({ note, searchQuery }) => {
  if (note.title && note.title.trim().length > 0) {
    return (
      <span className="truncate text-gray-800 dark:text-gray-200">
        {highlightText(note.title.trim(), searchQuery)}
      </span>
    );
  }

  if (note.encrypted) {
    return <EncryptedExcerpt />;
  }

  const contentType = note.content_type ?? 'MDX';

  switch (contentType) {
    case 'TXT':
      return <TxtExcerpt note={note} searchQuery={searchQuery} />;
    case 'MDX':
    default:
      return <MDXExcerpt note={note} searchQuery={searchQuery} />;
  }
};

export default NoteListTitle;
